import i18n from '@/i18n/i18n'
import FarmerDocumentService from '@/services/admin/FarmerDocumentService'
import { build_query, errorMessage } from '@/utilities/helpers.js'

import { DOCUMENT_TYPE_GROUPS, FARMER_DOCUMENT_TYPE } from '@/constants/farmerDocumentType.js'

const { t } = i18n.global
export const allowedSorts = 'created_at'
export const allowedAppends = 'document_verification_activity,mime_type'

export const namespaced = true

export const state = {
  documents: [],
  document: null,
  viewDocument: null,
  farmerDocuments: [],
  applicationDocuments: [],
  documentVersions: []
}

const checkDocumentExist = (documents, types) => {
  return documents
    .filter(doc => types.includes(doc.type))[0]
}

export const mutations = {
  SET_DOCUMENTS(state, documents) {
    state.documents = documents
  },
  SET_DOCUMENT(state, document) {
    state.document = document
  },
  SET_VIEW_DOCUMENT(state, viewDocument) {
    state.viewDocument = viewDocument
  },
  ADD_DOCUMENT(state, document) {
    state.documents.push(document)
  },
  DELETE_DOCUMENT(state, documentToRemove) {
    state.documents = state.documents.filter(document => {
      return document.id !== documentToRemove.id
    })
  },
  SET_FARMER_DOCUMENTS(state, farmerDocuments) {
    const idCard = checkDocumentExist(farmerDocuments, DOCUMENT_TYPE_GROUPS.ID_CARDS)

    if (!idCard) {
      farmerDocuments.push({
        type: FARMER_DOCUMENT_TYPE.NEW_ID_CARD,
        status: 0
      })
    }

    const registryCertificate = checkDocumentExist(farmerDocuments, DOCUMENT_TYPE_GROUPS.REGISTRY_CERTIFICATES)

    if (!registryCertificate) {
      farmerDocuments.push({
        type: FARMER_DOCUMENT_TYPE.FARMER_REGISTRY_CERTIFICATE,
        status: 0
      })
    }

    DOCUMENT_TYPE_GROUPS.INDIVIDUAL_DOCUMENTS.forEach(type => {
      const existingType = farmerDocuments.find(doc => doc.type === type)
      if (!existingType) {
        farmerDocuments.push({
          type: type,
          status: 0
        })
      }
    })

    let applicationDocuments = []

    DOCUMENT_TYPE_GROUPS.APPLICATION_DOCUMENTS.forEach(type => {
      const existingType = farmerDocuments.find(doc => doc.type === type)
      if (!existingType) {
        applicationDocuments.push({
          type: type,
          status: 0
        })
      } else {
        applicationDocuments.push(existingType)
        farmerDocuments.splice(farmerDocuments.indexOf(existingType), 1)
      }
    })
    

    state.applicationDocuments = applicationDocuments
    state.farmerDocuments = farmerDocuments
  },
  SET_DOCUMENT_VERSIONS(state, documentVersions) {
    state.documentVersions = documentVersions
  }
}

export const getters = {
  getDocumentById: state => id => {
    return state.documents ? state.documents.find(document => document.id === id) : false
  }
}

export const actions = {
  sendFarmerVerificationDocument({ dispatch, commit, state }, form) {
    return new Promise((resolve, reject) => {
      FarmerDocumentService.sendFarmerVerificationDocument(form)
        .then(response => {
          commit('ADD_DOCUMENT', response.data)

          const notification = {
            type: 'success',
            message: t('store.shared.added', {
              element: t('store.admin.farmers.document')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  updateFarmerDocumentStatus({ dispatch, commit }, params) {
    return new Promise((resolve, reject) => {
      FarmerDocumentService.updateFarmerDocumentStatus(params.farmerId, params.documentId, params.data)
        .then(response => {
          commit('ADD_DOCUMENT', response.data)

          dispatch('adminFarmer/refreshFarmer', params.farmerId, {
            root: true
          })

          const notification = {
            type: 'success',
            message: t('store.admin.farmers.update_farmer_document_status_success')
          }

          dispatch('notification/add', notification, {
            root: true
          })

          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  deleteFarmerDocument({ dispatch, commit }, params) {
    return new Promise((resolve, reject) => {
      FarmerDocumentService.deleteFarmerDocument(params.farmerId, params.documentId)
        .then(response => {
          commit('DELETE_DOCUMENT', response.data)

          dispatch('adminFarmer/refreshFarmer', params.farmerId, {
            root: true
          })

          const notification = {
            type: 'success',
            message: t('store.admin.farmers.document_deleted_successfully')
          }

          dispatch('notification/add', notification, {
            root: true
          })

          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getDocuments({ commit, dispatch }, id) {
    let query = {}

    query = build_query(query, null, null, allowedSorts, allowedAppends)

    return new Promise((resolve, reject) => {
      FarmerDocumentService.getDocuments(id, query)
        .then(response => {
          commit('SET_DOCUMENTS', response.data)

          resolve(response.data)
        })
        .catch(err => {
          const notifications = {
            type: 'danger',
            message: errorMessage(err)
          }
          dispatch('notification/add', notifications, {
            root: true
          })
          reject(err)
        })
    })
  },
  getDocument({ dispatch, commit }, params) {
    return new Promise((resolve, reject) => {
      FarmerDocumentService.getDocument(params.farmerId, params.documentId)
        .then(response => {
          commit('SET_DOCUMENT', response.data)
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  viewDocument({ dispatch, commit }, documentId) {
    return new Promise((resolve, reject) => {
      FarmerDocumentService.viewDocument(documentId)
        .then(response => {
          commit('SET_VIEW_DOCUMENT', response)
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: t('store.admin.farmers.document_getting_for_view_error') + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  listDocuments({ commit, dispatch }, farmerId) {
    return new Promise((resolve, reject) => {
      FarmerDocumentService.listDocuments(farmerId)
        .then(response => {
          commit('SET_FARMER_DOCUMENTS', response.data)
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  documentVersions({ commit, dispatch }, params) {
    return new Promise((resolve, reject) => {
      FarmerDocumentService.documentVersions(params.farmerId, params.documentId)
        .then(response => {
          commit('SET_DOCUMENT_VERSIONS', response.data)
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  updateDocumentStatus({ dispatch, commit }, params) {
    return new Promise((resolve, reject) => {
      FarmerDocumentService.updateDocumentStatus(params.farmerId, params.documentId, params.data)
        .then(response => {
          const notification = {
            type: 'success',
            message: t('store.admin.farmers.update_farmer_document_status_success')
          }

          dispatch('notification/add', notification, {
            root: true
          })

          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  }
}
